import { Link } from "react-router-dom";
import { MapPin, Users, ArrowUpRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import Highlight from "@/components/Highlight";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { API } from "@/lib/api";
import { ROLES, projectPath } from "@/lib/roles";
import { PROJECT_STATUSES, PROJECT_STATUS_MAP } from "@/lib/projectStatus";

export default function ProjectCard({ project, index, onStatusChange, query = "" }) {
  const percent = project.progress?.global_percent ?? 0;
  const budget = project.budget
    ? new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(project.budget)
    : null;
  const status = PROJECT_STATUS_MAP[project.status] || PROJECT_STATUS_MAP.en_gestion;
  const cover = project.cover_path;
  const muted = cover ? "text-white/85" : "text-muted-foreground";

  return (
    <Link
      to={projectPath(project.my_role, project.project_id)}
      data-testid={`project-card-${index}`}
      className={`group relative block overflow-hidden rounded-2xl border p-6 transition-all animate-fade-up hover:shadow-xl ${cover ? "border-black/10 text-white hover:border-white/60" : "border-border bg-card hover:border-primary/50 hover:shadow-lg"}`}
      style={{ animationDelay: `${0.06 * index}s` }}
    >
      {cover && (
        <>
          <img
            src={`${API}/files/${cover}`}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            data-testid={`project-card-cover-${index}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/35" />
        </>
      )}

      <div className="relative">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className={`caption mb-1.5 ${cover ? "text-white/70" : ""}`}>{ROLES[project.my_role]?.label}</div>
            <h3 className="text-xl font-semibold tracking-tight truncate"><Highlight text={project.name} query={query} /></h3>
            <p className={`text-sm mt-1 flex items-center gap-1.5 ${muted}`}>
              <MapPin className="h-3.5 w-3.5" /> <Highlight text={project.location} query={query} />
            </p>
          </div>
          <ArrowUpRight className={`h-5 w-5 shrink-0 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${cover ? "text-white/80 group-hover:text-white" : "text-muted-foreground group-hover:text-primary"}`} />
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-1.5">
            <span className={`text-xs ${muted}`}>Avance global</span>
            <span className="font-mono text-sm font-semibold">{percent}%</span>
          </div>
          <div className={`h-2 rounded-full overflow-hidden ${cover ? "bg-white/25" : "bg-secondary"}`}>
            <div className="h-full bg-primary rounded-full transition-all duration-700" style={{ width: `${percent}%` }} />
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between gap-2">
          <Badge variant="outline" className={`gap-1.5 font-mono font-normal ${cover ? "border-white/30 text-white/90 bg-white/10" : "text-muted-foreground"}`}>
            <Users className="h-3 w-3" /> {project.members_count} {project.members_count === 1 ? "usuario" : "usuarios"}
          </Badge>
          {budget && <span className={`font-mono text-sm ${cover ? "text-white/90" : "text-muted-foreground"}`}>{budget}</span>}
        </div>

        {onStatusChange ? (
          <div
            className={`mt-4 pt-4 border-t ${cover ? "border-white/20" : "border-border/60"}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          >
            <Select value={status.value} onValueChange={(v) => onStatusChange(project, v)}>
              <SelectTrigger className={`h-9 text-sm ${cover ? "bg-white/10 border-white/30 text-white backdrop-blur-sm" : ""}`} data-testid={`project-status-change-${index}`}>
                <span className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${status.dot}`} />
                  <SelectValue />
                </span>
              </SelectTrigger>
              <SelectContent>
                {PROJECT_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value} data-testid={`project-status-change-${index}-${s.value}`}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="mt-4 flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${status.dot}`} />
            <span className={`text-xs ${muted}`}>{status.label}</span>
          </div>
        )}
      </div>
    </Link>
  );
}
